// @flow
import React from 'react'
import type { Node } from 'react'
import {
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@material-ui/core'
import { isCreditCard, isNumeric, isLength } from 'validator'
import { filter, each } from 'lodash/collection'
import { get } from 'lodash/object'
import { last } from 'lodash/array'
import { isEmpty } from 'lodash/lang'
import i18n from 'i18next'
import CreditCardPicker from './CreditCardPicker'
import CreditCardForm from './CreditCardForm'
import Button from '../../../../master/components/styled/ButtonBase'

type Props = {
  creditCards: Array<{
    expirationMonth: string,
    expirationYear: string,
    holder: string,
    number: string,
    type: string
  }>,
  postPaymentRecurrent: Function,
  postPaymentCreditCard: Function,
  isFetchingCheckoutPayment: boolean,
  hasCreditCards: boolean,
  children?: Node
}
type FormField = {
  error: boolean,
  isRequired: boolean,
  isValid?: Function,
  value: string
}

type State = {
  paymentMethod: string,
  recurrent: {
    formFields: {
      creditCard: FormField
    }
  },
  newCreditCard: {
    formFields: {
      holder: FormField,
      number: FormField,
      expirationMonth: FormField,
      expirationYear: FormField,
      cvv: FormField,
      remember: FormField
    }
  }
}

const PAYMENT_METHODS = {
  RECURRENT: 'recurrent',
  NEW_CREDIT_CARD: 'newCreditCard'
}

export class PaymentMethod extends React.Component<Props, State> {
  state = {
    paymentMethod: this.props.hasCreditCards
      ? PAYMENT_METHODS.RECURRENT
      : PAYMENT_METHODS.NEW_CREDIT_CARD,
    [PAYMENT_METHODS.RECURRENT]: {
      formFields: {
        creditCard: {
          value: get(last(this.props.creditCards), 'number', ''),
          error: false,
          isRequired: true,
          isValid: (creditCard: string) => !isEmpty(creditCard)
        }
      }
    },
    [PAYMENT_METHODS.NEW_CREDIT_CARD]: {
      formFields: {
        holder: {
          error: false,
          isRequired: true,
          isValid: (holder: string) => !isEmpty(holder),
          value: ''
        },
        number: {
          error: false,
          isRequired: true,
          isValid: (number: string) => isCreditCard(number),
          value: ''
        },
        expirationMonth: {
          error: false,
          isRequired: true,
          isValid: (month: string) =>
            isNumeric(month) && isLength(month, { min: 1, max: 2 }),
          value: ''
        },
        expirationYear: {
          error: false,
          isRequired: true,
          isValid: (year: string) =>
            isNumeric(year) && isLength(year, { min: 2, max: 2 }),
          value: ''
        },
        cvv: {
          error: false,
          isRequired: true,
          isValid: (cvv: string) =>
            isNumeric(cvv) && isLength(cvv, { min: 3, max: 4 }),
          value: ''
        },
        remember: {
          error: false,
          isRequired: false,
          value: ''
        }
      }
    }
  }

  handleRadioGroupChange = (event: Object, paymentMethod: string) => {
    this.setState({ paymentMethod: event.target.value })
  }

  onInputChange: Function = (event: Object) => {
    const paymentMethod = this.state.paymentMethod.toString()
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value

    this.setState({
      [paymentMethod]: {
        ...this.state[paymentMethod],
        formFields: {
          ...this.state[paymentMethod].formFields,
          [event.target.name]: {
            ...this.state[paymentMethod].formFields[event.target.name],
            value
          }
        }
      }
    })
  }

  handleInputErrors: Function = (formFields: Object) => {
    each(Object.keys(formFields), fieldKey => {
      const field = formFields[fieldKey]
      const paymentMethod = this.state.paymentMethod.toString()

      field.isRequired &&
        this.setState(state => ({
          [paymentMethod]: {
            ...state[paymentMethod],
            formFields: {
              ...state[paymentMethod].formFields,
              [fieldKey]: {
                ...state[paymentMethod].formFields[fieldKey],
                error: !field.isValid(field.value)
              }
            }
          }
        }))
    })
  }

  onSubmit: Function = (event: Object) => {
    event.preventDefault()

    const postPaymentMethod = (paymentMethod: string) => {
      switch (paymentMethod) {
        case PAYMENT_METHODS.RECURRENT:
          return this.props.postPaymentRecurrent(
            this.state.recurrent.formFields.creditCard.value,
            i18n.t('routes.CHECKOUT_THANKS')
          )
        case PAYMENT_METHODS.NEW_CREDIT_CARD:
          return this.props.postPaymentCreditCard(
            {
              holder: this.state.newCreditCard.formFields.holder.value,
              number: this.state.newCreditCard.formFields.number.value,
              cvv: this.state.newCreditCard.formFields.cvv.value,
              expirationMonth: this.state.newCreditCard.formFields
                .expirationMonth.value,
              expirationYear: this.state.newCreditCard.formFields.expirationYear
                .value,
              remember: this.state.newCreditCard.formFields.remember.value
            },
            i18n.t('routes.CHECKOUT_THANKS')
          )
        default:
          return null
      }
    }

    isEmpty(
      filter(
        this.state[this.state.paymentMethod].formFields,
        field =>
          field.isRequired && (!field.value || !field.isValid(field.value))
      )
    )
      ? postPaymentMethod(this.state.paymentMethod)
      : this.handleInputErrors(this.state[this.state.paymentMethod].formFields)
  }

  render () {
    return (
      <Grid container spacing={16}>
        {!this.props.hasCreditCards ? (
          <CreditCardForm
            onInputChange={this.onInputChange}
            onSubmit={this.onSubmit}
            newCreditCard={this.state.newCreditCard}
          />
        ) : (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <RadioGroup
                value={this.state.paymentMethod}
                onChange={this.handleRadioGroupChange}
              >
                <FormControlLabel
                  value={PAYMENT_METHODS.RECURRENT}
                  control={<Radio color='primary' />}
                  label={i18n.t('checkout.label.selectCreditCard')}
                  labelPlacement='end'
                />
                {this.state.paymentMethod === PAYMENT_METHODS.RECURRENT && (
                  <CreditCardPicker
                    recurrent={this.state.recurrent}
                    creditCards={this.props.creditCards}
                    onInputChange={this.onInputChange}
                    onSubmit={this.onSubmit}
                  />
                )}
                <FormControlLabel
                  value={PAYMENT_METHODS.NEW_CREDIT_CARD}
                  control={<Radio color='primary' />}
                  label={i18n.t('checkout.label.addNewCreditCard')}
                  labelPlacement='end'
                />
                {this.state.paymentMethod ===
                  PAYMENT_METHODS.NEW_CREDIT_CARD && (
                  <CreditCardForm
                    onInputChange={this.onInputChange}
                    onSubmit={this.onSubmit}
                    newCreditCard={this.state.newCreditCard}
                  />
                )}
              </RadioGroup>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={12} md={12}>
          {this.props.children}
          <Button
            disabled={this.props.isFetchingCheckoutPayment}
            onClick={this.onSubmit}
            type='submit'
          >
            {this.props.isFetchingCheckoutPayment
              ? i18n.t('checkout.processingPayment')
              : i18n.t('checkout.makePayment')}
          </Button>
        </Grid>
      </Grid>
    )
  }
}

export default PaymentMethod
